import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Banknote, TrendingUp, PiggyBank, Percent } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { businessMonthKey, businessToday } from '@/lib/format';

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

export default function LoansAnalytics() {
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });

    const fromDate = format(monthRange.from, 'yyyy-MM-dd');
    const toDate = format(monthRange.to, 'yyyy-MM-dd');

    // Fetch summary statistics
    const { data: summaryData, isLoading: summaryLoading } = useQuery({
        queryKey: ['loans-analytics-summary', fromDate, toDate],
        queryFn: async () => {
            const { data: loans, error } = await supabase
                .from('loans')
                .select('id, interest_rate, transactions(amount, transaction_at)')
                .gte('created_at', fromDate)
                .lte('created_at', toDate + 'T23:59:59');
            if (error) throw error;

            // Get repayments for the loans
            const loanIds = loans?.map(l => l.id) || [];
            let totalRepaid = 0;

            if (loanIds.length > 0) {
                const { data: repayments, error: repError } = await supabase
                    .from('loan_repayments')
                    .select('loan_id, transactions(amount)')
                    .in('loan_id', loanIds);
                if (repError) throw repError;
                totalRepaid = repayments?.reduce((sum, r) => sum + (r.transactions?.amount || 0), 0) || 0;
            }

            const totalLoans = loans?.length || 0;
            const totalPrincipal = loans?.reduce((sum, l) => sum + (l.transactions?.amount || 0), 0) || 0;
            const avgInterestRate = totalLoans > 0
                ? loans.reduce((sum, l) => sum + (l.interest_rate || 0), 0) / totalLoans
                : 0;
            const totalWithInterest = loans?.reduce((sum, l) => {
                const principal = l.transactions?.amount || 0;
                return sum + principal * (1 + (l.interest_rate || 0) / 100);
            }, 0) || 0;
            const outstandingBalance = totalWithInterest - totalRepaid;

            return {
                totalLoans,
                totalPrincipal,
                totalWithInterest,
                avgInterestRate,
                totalRepaid,
                outstandingBalance,
                collectionRate: totalWithInterest > 0 ? (totalRepaid / totalWithInterest) * 100 : 0,
            };
        },
    });

    // Fetch monthly trend data
    const { data: monthlyData, isLoading: monthlyLoading } = useQuery({
        queryKey: ['loans-analytics-monthly', fromDate, toDate],
        queryFn: async () => {
            const { data: loans, error } = await supabase
                .from('loans')
                .select('id, created_at, transactions(amount, transaction_at)')
                .gte('created_at', fromDate)
                .lte('created_at', toDate + 'T23:59:59')
                .order('created_at', { ascending: true });
            if (error) throw error;

            const { data: repayments, error: repError } = await supabase
                .from('loan_repayments')
                .select('created_at, transactions(amount)')
                .gte('created_at', fromDate)
                .lte('created_at', toDate + 'T23:59:59');
            if (repError) throw repError;

            // Generate month keys
            const monthlyMap = new Map<string, { disbursed: number; repaid: number }>();
            const current = new Date(monthRange.from.getFullYear(), monthRange.from.getMonth(), 1);
            while (current <= monthRange.to) {
                const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
                monthlyMap.set(key, { disbursed: 0, repaid: 0 });
                current.setMonth(current.getMonth() + 1);
            }

            loans?.forEach(l => {
                // The stored Ghana month, as the sibling Analytics files bucket it. Reading local
                // parts off the instant moved a disbursement recorded within the viewer's offset of
                // a month boundary into the neighbouring bar.
                const key = businessMonthKey(l.created_at);
                if (key && monthlyMap.has(key)) {
                    monthlyMap.get(key)!.disbursed += l.transactions?.amount || 0;
                }
            });

            repayments?.forEach(r => {
                const key = businessMonthKey(r.created_at);
                if (key && monthlyMap.has(key)) {
                    monthlyMap.get(key)!.repaid += r.transactions?.amount || 0;
                }
            });

            return Array.from(monthlyMap.entries()).map(([month, data]) => ({
                month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
                disbursed: data.disbursed,
                repaid: data.repaid,
            }));
        },
    });

    if (summaryLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
                </div>
                <Skeleton className="h-80" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Disbursed</CardTitle>
                        <Banknote className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(summaryData?.totalPrincipal || 0)}</div>
                        <p className="text-xs text-muted-foreground">{summaryData?.totalLoans || 0} advances</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Repaid</CardTitle>
                        <TrendingUp className="h-4 w-4 text-green-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-green-600">{formatCurrency(summaryData?.totalRepaid || 0)}</div>
                        <p className="text-xs text-muted-foreground">{summaryData?.collectionRate.toFixed(1)}% collection rate</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Outstanding</CardTitle>
                        <PiggyBank className="h-4 w-4 text-orange-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-orange-600">{formatCurrency(summaryData?.outstandingBalance || 0)}</div>
                        <p className="text-xs text-muted-foreground">Principal + Interest</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Avg Interest Rate</CardTitle>
                        <Percent className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{summaryData?.avgInterestRate.toFixed(1)}%</div>
                        <p className="text-xs text-muted-foreground">Average across advances</p>
                    </CardContent>
                </Card>
            </div>

            {/* Charts */}
            <div className="grid gap-6 lg:grid-cols-1">
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Advance Disbursement vs Repayment</CardTitle>
                        <CardDescription>Monthly comparison of advances given vs repayments received</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {monthlyLoading ? (
                            <Skeleton className="h-64" />
                        ) : (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={monthlyData}>
                                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                    <XAxis dataKey="month" className="text-xs" />
                                    <YAxis className="text-xs" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                    <Tooltip
                                        formatter={(value: number) => formatCurrency(value)}
                                        contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                                    />
                                    <Legend />
                                    <Bar dataKey="disbursed" name="Disbursed" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="repaid" name="Repaid" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
